import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import axios from "axios";
import { translate } from "../i18n";
import { nameRegex, youtubeRegex } from "./regex";
import { newAuthService } from "Service/Axios";
import { GLOBAL_FILE_UPLOAD_URL } from "Constant/api";
import { ISO_DATE, passwordRules } from "Constant/constants";

export const getFirstLetterOfName = (user) => {
  if (user && user.firstName && user.lastName) {
    return (
      <div
        className="short-name d-flex justify-content-center align-items-center text-uppercase w-100 h-100"
        style={{
          backgroundColor: user.profileColor ? user.profileColor : "#182A58",
        }}
      >
        {`${user.firstName.charAt(0)} ${user.lastName.charAt(0)}`}
      </div>
    );
  }
};

export const dynamicKeySortDescending = (data, key) => {
  const sortedData = data.sort((a, b) => {
    const typeA = a[key]?.type;
    let valueA = "";
    let valueB = "";

    if (typeA === "number") {
      valueA = Number(a[key]?.value) || 0;
      valueB = Number(b[key]?.value) || 0;
    } else {
      valueA = a[key]?.value ? a[key].value.toUpperCase() : "";
      valueB = b[key]?.value ? b[key].value.toUpperCase() : "";
    }

    if (valueA > valueB) {
      return -1;
    }
    if (valueA < valueB) {
      return 1;
    }
    return 0;
  });
  return sortedData;
};

export const dynamicKeySortAscending = (data, key) => {
  const sortedData = data.sort((a, b) => {
    const typeA = a[key]?.type;
    let valueA = "";
    let valueB = "";

    if (typeA === "number") {
      valueA = Number(a[key]?.value) || 0;
      valueB = Number(b[key]?.value) || 0;
    } else {
      valueA = a[key]?.value ? a[key].value.toUpperCase() : "";
      valueB = b[key]?.value ? b[key].value.toUpperCase() : "";
    }

    if (valueA < valueB) {
      return -1;
    }
    if (valueA > valueB) {
      return 1;
    }
    return 0;
  });
  return sortedData;
};

export const convertDynamicArrayByKey = (response) => {
  let newArr = [];
  const objFieldDetails = response.map(
    ({ fieldDetails }) => fieldDetails || []
  );
  objFieldDetails.forEach((item, index) => {
    let obj = {};
    obj["id"] = response[index].gid;
    item.forEach((it, iy) => {
      obj[it.name] = it;
    });
    newArr.push(obj);
  });
  return newArr;
};

export const convertDynamicArrayByKeyWithUser = (response) => {
  let newArr = [];
  const objFieldDetails = response.map(({ fieldDetails }) => fieldDetails);
  objFieldDetails.forEach((item, index) => {
    let obj = {};
    obj["id"] = response[index].gid;
    obj["firstName"] = response[index].firstName;
    obj["lastName"] = response[index].lastName;
    obj["photoURL"] = response[index].photoURL;
    item.forEach((it, iy) => {
      obj[it.name] = it;
    });
    newArr.push(obj);
  });
  return newArr;
};

export const convertLinkObjectListToJoin = (list, keyName) => {
  return list?.length
    ? list.map((item) => item[keyName] || item?.name || "").join(", ")
    : "";
};

// Replace line breaks with <br/>
export const convertTextToBreak = (text) => {
  const textWithLineBreaks = text.replace(/\n/g, "<br/>");
  return textWithLineBreaks;
};

// Replace URLs with anchor tags
export const convertTextToLink = (text) => {
  const textWithLineBreaks = convertTextToBreak(text);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const textWithLinks = textWithLineBreaks.replace(
    urlRegex,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );
  return textWithLinks;
};

export const convertYoutubeLinkToEmbed = (text) => {
  if (youtubeRegex.test(text)) {
    const youtubeId = text.match(youtubeRegex)[1];
    return `https://www.youtube.com/embed/${youtubeId}`;
  } else {
    return text;
  }
};

// Convert HTML to Text
export const convertHTMLToText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const plainText = div.textContent || div.innerText;
  return plainText;
};

// Convert escaped quotes to normal quotes
export const convertStringToHTML = (htmlString) => {
  return htmlString.replace(/\\"/g, '"');
};

// Strip HTML tags
export const convertStripHTML = (html) => {
  return html ? html.replace(/<[^>]+>/g, "") : "";
};

// Update
export function replaceArrByNewObject(array, newObj) {
  // Find the index of the object with matching ID
  const index = array.findIndex((obj) => obj.id === newObj.id);

  // If index found, replace the object, else push the new object
  if (index !== -1) {
    array[index] = newObj;
  } else {
    array.push(newObj);
  }
  return array;
}

export const replaceSpaceWithDash = (string) => {
  const str = string && string.replace(/\s+/g, "-").toLowerCase();
  return str;
};

export const getISODateString = (givenDate) => {
  return givenDate ? new Date(givenDate).toISOString() : ISO_DATE;
};

const fullMonthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const shortMonthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const shortDayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// April 12, 2023 21:10
export const formatDateWithFullMonthTime = (givenDate) => {
  const date = new Date(givenDate);
  const month = fullMonthNames[date.getMonth()];
  const year = date.getFullYear();
  const day = date.getDate() > 10 ? `${date.getDate()}` : `0${date.getDate()}`;
  const hh =
    date.getHours() > 10 ? `${date.getHours()}` : `0${date.getHours()}`;
  const mm =
    date.getMinutes() > 10 ? `${date.getMinutes()}` : `0${date.getMinutes()}`;
  const formatDate = `${month} ${day}, ${year} ${hh}:${mm}`;
  return formatDate;
};

// April 23, 2023
export const formatDateWithFullMonth = (givenDate) => {
  const date = new Date(givenDate);
  const month = fullMonthNames[date.getMonth()];
  const year = date.getFullYear();
  const day = date.getDate();
  const formatDate = `${month} ${day}, ${year}`;
  return formatDate;
};

// April 2023
export const formatMonthYear = (givenDate) => {
  const date = new Date(givenDate);
  const month = fullMonthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${year}`;
};

export const formatYear = (givenDate) => {
  const date = new Date(givenDate);
  const year = date.getFullYear();
  return `${year}`;
};

// Apr 23, 2023
export const formatDateWithShortMonth = (givenDate) => {
  const date = new Date(givenDate);
  const month = shortMonthNames[date.getMonth()];
  const year = date.getFullYear();
  const day = date.getDate();
  const formatDate = `${month} ${day}, ${year}`;
  return formatDate;
};

// 12:00 Am
export const formatDateWithTime = (givenDate) => {
  const date = new Date(givenDate);
  const hour = date.getHours();
  const minute = date.getMinutes();
  const amPm = hour < 12 ? "AM" : "PM";
  const timeString = `${hour}:${minute} ${amPm}`;
  return timeString;
};

// 2023-01-01
export const formatDateLocal = (givenDate) => {
  const today = new Date(givenDate);
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0"); // January is 0
  const year = today.getFullYear();
  return `${year}-${month}-${day}`;
};

export const formatWithCommas = (num) => {
  if (!num) return "";
  return num.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const removeCommas = (value) => {
  return value.replace(/,/g, "");
};

export const disablePastDate = (date) => {
  if (!date) return undefined;
  const selectedDate = new Date(date);
  return new Date(selectedDate).toISOString().split("T")[0];
};

export const getCurrentWeekList = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const day = 1; //
  const firstDayOfWeek = new Date(
    today.getTime() - ((dayOfWeek - day + 7) % 7) * 24 * 60 * 60 * 1000
  );
  const weekDates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(firstDayOfWeek.getTime() + i * 24 * 60 * 60 * 1000);
    const month = shortMonthNames[date.getMonth()];
    const day = shortDayNames[date.getDay()];

    const weekDate = {
      month,
      date: date.getDate(),
      day,
      localDate: formatDateLocal(date),
    };
    weekDates.push(weekDate);
  }
  return weekDates;
};

export const compareDates = (date1, date2) => {
  if (!date1 || !date2) return false;
  const givenDate = new Date(date1);
  const currentData = new Date(date2);
  return givenDate.getTime() > currentData.getTime();
};

//
export const getFileNameFromUrl = (value) => {
  const url = new URL(value);
  // eslint-disable-next-line
  const fileName = url && url.pathname.replace(/^.*[\\\/]/, "");
  return fileName;
};

export const downloadFile = (value, fileName) => {
  const blob = new Blob([value]);
  const link = document.createElement("a");
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const newDownloadFile = (value) => {
  const matches = value.match(/\/([^\/]+)$/);

  if (matches && matches.length > 1) {
    const filenameWithExtension = matches[1];
    // Output: Changes.docx

    // If you want to separate the filename and extension:
    // const [filename, extension] = filenameWithExtension.split(".");

    fetch(value)
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filenameWithExtension;
        link.click();
        URL.revokeObjectURL(url);
      });
  } else {
    toast.error("Filename not found in URL.");
  }
};

export const viewPdfFileNewWindow = async (url) => {
  const response = await axios.get(url, {
    responseType: "arraybuffer",
  });

  const blob = new Blob([response.data], { type: "application/pdf" });
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank");
};

export const handleAlphabets = (e) => {
  if (e.key.length === 1 && !nameRegex.test(e.key)) {
    e.preventDefault();
  }
};

export const handleNumberWithTwoDecimal = (e) => {
  if (
    (e.key >= "0" && e.key <= "9") ||
    e.key === "." ||
    e.key === "Backspace" ||
    e.key === "Delete"
  ) {
    // Check if the entered value will exceed two decimal places
    if (
      e.key === "." &&
      e.target.value.includes(".") // Only allow one decimal point
    ) {
      e.preventDefault();
    } else if (
      e.target.value.includes(".") &&
      e.target.value.split(".")[1].length >= 2 &&
      e.key !== "Backspace" &&
      e.key !== "Delete"
    ) {
      e.preventDefault();
    }
  } else {
    e.preventDefault();
  }
};

export const handleFilterListBySearch = (list, key, value) => {
  const searchedResult = list.filter((option) =>
    option[key].toLowerCase().includes(value.toLowerCase())
  );
  return searchedResult;
};

export const encryptFun = (v) => {
  var data = v;
  var key = CryptoJS.enc.Latin1.parse("1234567812345678");
  var iv = CryptoJS.enc.Latin1.parse("1234567812345678");
  var encrypted = CryptoJS.AES.encrypt(data, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return encrypted;
};

// Upload
export const uploadHandler = async (
  e,
  moduleType,
  allowdFiles,
  apiName,
  taskIds
) => {
  const file = e.target.files[0];
  const fileParts = file.type.split("/");
  const fileType = fileParts[1];
  const fileName = file.name;
  const uploadDate = getISODateString();
  let fileSize = 0;

  if (file.size < 1000000) {
    fileSize = Math.floor(file.size / 1000) + " " + "KB";
  } else {
    fileSize = Math.floor(file.size / 1000000) + " " + "MB";
  }
  if (allowdFiles.includes(fileType)) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("appType", moduleType);
    formData.append("uploadDate", uploadDate);
    if (taskIds && taskIds.length > 0) {
      formData.append("taskIds", taskIds);
    }
    try {
      const response = await newAuthService(
        apiName ? apiName : GLOBAL_FILE_UPLOAD_URL,
        "POST",
        formData
      );
      if (response.status && response.data) {
        const uploadObj = {
          gid: response.data.gid,
          url: response.data.url,
          appType: response.data.appType,
          uploadDate,
          name: fileName,
          size: fileSize,
          filetype: fileType,
        };
        return uploadObj;
      } else {
        toast.error(
          translate("common.messages.notification.failed.somethingWentWrong")
        );
        return null;
      }
    } catch {
      toast.error(
        translate("common.messages.notification.failed.somethingWentWrong")
      );
      return null;
    }
  } else {
    toast.error(translate("common.messages.notification.failed.wrongFileType"));
  }
};

export const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

export const checkPasswordStrength = (
  password,
  firstName = "",
  lastName = "",
  email = ""
) => {
  const conditions = [
    { regex: /.{8,}/, message: passwordRules[1] },
    { regex: /[A-Z]/, message: passwordRules[2] },
    { regex: /[a-z]/, message: passwordRules[3] },
    { regex: /\d/, message: passwordRules[4] },
    { regex: /[!@#$%^&*]/, message: passwordRules[5] },
  ];

  const lowerPassword = password.toLowerCase();
  const includesPersonalInfo =
    (firstName && lowerPassword.includes(firstName.toLowerCase())) ||
    (lastName && lowerPassword.includes(lastName.toLowerCase())) ||
    (email && lowerPassword.includes(email.toLowerCase()));

  const errors = [];

  if (includesPersonalInfo) {
    errors.push(passwordRules[0]);
  }

  // Check which conditions are NOT met and add their messages to errors
  const passedConditions = conditions.filter((c) => {
    const result = c.regex.test(password);
    if (!result) errors.push(c.message);
    return result;
  });

  let strength;

  if (!password) {
    strength = { label: "Weak", value: 0, errors: [] };
  } else if (includesPersonalInfo) {
    strength = { label: "Weak", value: 25, errors };
  } else if (passedConditions.length === conditions.length) {
    strength = { label: "Strong", value: 100, errors };
  } else if (passedConditions.length >= 3 && password.length >= 8) {
    strength = { label: "Medium", value: 50, errors };
  } else {
    strength = { label: "Weak", value: 25, errors };
  }

  return strength;
};

export const validateTextEdiorContent = (content) => {
  if (!content || content.trim() === "") {
    return "";
  }
  return content.replace(/(<p><br><\/p>)+$/g, "");
};

export const saveRedirectAfterLogin = (path) => {
  localStorage.setItem("redirectAfterLogin", path);
};
